import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api";
import { fmtDateTime } from "../../lib/format";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  ErrorBanner,
  SuccessBanner,
} from "../../components/ui";

type QType = "short_text" | "paragraph" | "select";

interface Question {
  id?: string;
  section: string | null;
  questionText: string;
  questionType: QType;
  options: string[] | null;
  required: boolean;
}

interface Survey {
  id: string;
  title: string;
  description: string | null;
  status: "draft" | "published" | "closed";
  questions: Question[];
  responseCount: number;
}

const TYPE_LABELS: Record<QType, string> = {
  short_text: "短文",
  paragraph: "段落",
  select: "選択式",
};

interface ResponseRow {
  id: string;
  memberId: string;
  memberNo: string;
  displayName: string;
  email: string;
  submittedAt: string;
}

interface AnswerRow {
  questionId: string;
  section: string | null;
  questionText: string;
  answerText: string | null;
}

export function AdminSurveyPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"edit" | "responses">("edit");
  // 編集中の質問
  const [questions, setQuestions] = useState<Question[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // 回答
  const [responses, setResponses] = useState<ResponseRow[]>([]);
  const [detail, setDetail] = useState<{ row: ResponseRow; answers: AnswerRow[] } | null>(null);

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const r = await api.get<{ survey: Survey | null }>(
        `/admin/survey/by-event/${eventId}`,
      );
      setSurvey(r.survey);
      if (r.survey) {
        setQuestions(r.survey.questions);
        setTitle(r.survey.title);
        setDescription(r.survey.description ?? "");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [eventId]);

  const createSurvey = async (useTemplate: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/admin/survey/by-event/${eventId}`, { useTemplate });
      setMsg("アンケートを作成しました");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveQuestions = async () => {
    if (!survey) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.patch(`/admin/survey/${survey.id}`, { title, description: description || null });
      await api.put(`/admin/survey/${survey.id}/questions`, {
        questions: questions.map((q) => ({
          section: q.section,
          questionText: q.questionText,
          questionType: q.questionType,
          options: q.questionType === "select" ? q.options ?? [] : null,
          required: q.required,
        })),
      });
      setMsg("保存しました");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: "draft" | "published" | "closed") => {
    if (!survey) return;
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/admin/survey/${survey.id}`, { status });
      setMsg(status === "published" ? "公開しました" : status === "closed" ? "締め切りました" : "下書きに戻しました");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const loadResponses = async () => {
    if (!survey) return;
    const r = await api.get<{ items: ResponseRow[] }>(`/admin/survey/${survey.id}/responses`);
    setResponses(r.items);
  };

  const openDetail = async (row: ResponseRow) => {
    if (!survey) return;
    const r = await api.get<{ answers: AnswerRow[] }>(
      `/admin/survey/${survey.id}/responses/${row.memberId}`,
    );
    setDetail({ row, answers: r.answers });
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <Link to="/admin/events" className="text-sm text-tora underline">
        ← イベント管理へ戻る
      </Link>
      <SectionHeading level={2}>アンケート管理</SectionHeading>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      {msg && <SuccessBanner>{msg}</SuccessBanner>}

      {!survey ? (
        <div className="card p-6 space-y-4">
          <p className="text-sm text-tora-ink/70">
            このイベントにはまだアンケートがありません。作成方法を選んでください。
          </p>
          <div className="flex gap-2 flex-wrap">
            <button className="btn-primary" onClick={() => createSurvey(true)} disabled={busy}>
              標準テンプレート（17問）で作成
            </button>
            <button className="btn-outline" onClick={() => createSurvey(false)} disabled={busy}>
              空のアンケートで作成
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ステータス + タブ */}
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge
              tone={survey.status === "published" ? "success" : survey.status === "closed" ? "neutral" : "warning"}
              bold
            >
              {survey.status === "published" ? "公開中" : survey.status === "closed" ? "締切" : "下書き"}
            </StatusBadge>
            <span className="text-sm text-tora-ink/60">回答 {survey.responseCount} 件</span>
            <div className="ml-auto flex gap-2">
              {survey.status !== "published" && (
                <button className="btn-primary text-sm" onClick={() => setStatus("published")} disabled={busy}>
                  公開する
                </button>
              )}
              {survey.status === "published" && (
                <button className="btn-outline text-sm" onClick={() => setStatus("closed")} disabled={busy}>
                  締め切る
                </button>
              )}
              {survey.status === "closed" && (
                <button className="btn-outline text-sm" onClick={() => setStatus("published")} disabled={busy}>
                  再公開
                </button>
              )}
            </div>
          </div>

          {/* 回答URL */}
          {survey.status === "published" && (
            <div className="card p-3 text-xs">
              <p className="font-semibold text-tora-ink/80 mb-1">回答URL（LINE/メールで配布）</p>
              <div
                className="bg-black/[0.03] border rounded p-2 font-mono break-all select-all cursor-pointer"
                onClick={() =>
                  navigator.clipboard?.writeText(`${window.location.origin}/events/${eventId}/survey`)
                }
                title="クリックでコピー"
              >
                {window.location.origin}/events/{eventId}/survey
              </div>
              <p className="text-tora-ink/50 mt-1">※ 受付（QRチェックイン）済みの会員のみ回答できます。</p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === "edit" ? "bg-tora text-white" : "bg-black/5 text-tora-ink/70"}`}
              onClick={() => setTab("edit")}
            >
              質問編集
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === "responses" ? "bg-tora text-white" : "bg-black/5 text-tora-ink/70"}`}
              onClick={() => { setTab("responses"); loadResponses(); }}
            >
              回答一覧
            </button>
          </div>

          {tab === "edit" ? (
            <div className="card p-5 space-y-4">
              <div>
                <label className="label">タイトル</label>
                <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div>
                <label className="label">説明</label>
                <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>

              <div className="space-y-3">
                {questions.map((q, i) => (
                  <div key={i} className="border border-black/10 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-tora-ink/40 font-mono">Q{i + 1}</span>
                      <input
                        className="input text-xs max-w-[180px]"
                        placeholder="セクション(任意)"
                        value={q.section ?? ""}
                        onChange={(e) => updateQ(i, { section: e.target.value || null })}
                      />
                      <select
                        className="input text-xs max-w-[110px]"
                        value={q.questionType}
                        onChange={(e) => updateQ(i, { questionType: e.target.value as QType })}
                      >
                        <option value="short_text">短文</option>
                        <option value="paragraph">段落</option>
                        <option value="select">選択式</option>
                      </select>
                      <label className="text-xs flex items-center gap-1 ml-auto">
                        <input
                          type="checkbox"
                          checked={q.required}
                          onChange={(e) => updateQ(i, { required: e.target.checked })}
                        />
                        必須
                      </label>
                      <button className="text-red-600 text-xs" onClick={() => removeQ(i)}>削除</button>
                    </div>
                    <input
                      className="input"
                      placeholder="質問文"
                      value={q.questionText}
                      onChange={(e) => updateQ(i, { questionText: e.target.value })}
                    />
                    {q.questionType === "select" && (
                      <input
                        className="input text-xs"
                        placeholder="選択肢をカンマ区切りで (例: はい,迷っている,いいえ)"
                        value={(q.options ?? []).join(",")}
                        onChange={(e) =>
                          updateQ(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })
                        }
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 flex-wrap">
                <button className="btn-outline text-sm" onClick={addQ}>+ 質問を追加</button>
                <button className="btn-primary ml-auto" onClick={saveQuestions} disabled={busy}>
                  {busy ? "保存中…" : "保存"}
                </button>
              </div>
              <p className="text-xs text-tora-ink/50">
                ※ 質問を変更しても既存の回答は保持されます（質問文はスナップショット保存）。
              </p>
            </div>
          ) : (
            <div className="card p-4">
              {responses.length === 0 ? (
                <p className="text-sm text-tora-ink/50">まだ回答はありません</p>
              ) : (
                <div className="overflow-x-auto">
                  <div className="mb-3">
                    <a
                      href={`/api/admin/survey/${survey.id}/export.csv`}
                      className="btn-outline text-sm"
                      download
                    >
                      ⬇ CSV ダウンロード
                    </a>
                  </div>
                  <table className="w-full text-sm border border-black/10">
                    <thead className="bg-black/5 text-left">
                      <tr>
                        <th className="p-2">会員番号</th>
                        <th className="p-2">氏名</th>
                        <th className="p-2">メール</th>
                        <th className="p-2">回答日時</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {responses.map((r) => (
                        <tr key={r.id} className="border-t border-black/10">
                          <td className="p-2 font-mono text-xs">{r.memberNo}</td>
                          <td className="p-2">{r.displayName}</td>
                          <td className="p-2 text-xs">{r.email}</td>
                          <td className="p-2 text-xs">{fmtDateTime(r.submittedAt)}</td>
                          <td className="p-2">
                            <button className="text-tora underline text-xs" onClick={() => openDetail(r)}>
                              回答を見る
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* 個別回答モーダル */}
      {detail && (
        <div
          className="fixed inset-0 bg-black/40 flex items-start justify-center p-4 z-50 overflow-y-auto"
          onClick={() => setDetail(null)}
        >
          <div className="card max-w-2xl w-full p-6 my-8 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center">
              <h3 className="font-bold text-lg">{detail.row.displayName} さんの回答</h3>
              <button className="ml-auto text-sm text-tora underline" onClick={() => setDetail(null)}>
                閉じる
              </button>
            </div>
            <p className="text-xs text-tora-ink/50">{fmtDateTime(detail.row.submittedAt)} 回答</p>
            <dl className="space-y-3">
              {detail.answers.map((a) => (
                <div key={a.questionId} className="border-b border-black/5 pb-2">
                  <dt className="text-sm font-semibold text-tora-ink/80">{a.questionText}</dt>
                  <dd className="text-sm text-tora-ink/90 whitespace-pre-wrap mt-1">
                    {a.answerText && a.answerText.trim() !== "" ? a.answerText : <span className="text-tora-ink/30">（未回答）</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );

  function updateQ(i: number, patch: Partial<Question>) {
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  }
  function removeQ(i: number) {
    setQuestions((qs) => qs.filter((_, idx) => idx !== i));
  }
  function addQ() {
    setQuestions((qs) => [
      ...qs,
      { section: null, questionText: "", questionType: "paragraph", options: null, required: false },
    ]);
  }
}
